import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "components/common/Toast";

const formatNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

// Per-provider title + help text. The "nedco" voucher model splits a single
// service fee, so its wording differs from the add-on-fee bills.
const PROVIDER_META = {
  mada: {
    title: "Mada Commission",
    intro:
      "Configure the commissions charged on a Mada bill payment. The seller is charged: bill amount + user commission + system commission. Admin only.",
    descriptions: {
      system: "Platform service fee added on top of the seller cost.",
      user: "Commission added to the bill amount for the seller.",
    },
  },
  paltel: {
    title: "Paltel Commission",
    intro:
      "Configure the commissions charged on a Paltel bill payment. The seller is charged: bill amount + user commission + system commission. Admin only.",
    descriptions: {
      system: "Platform service fee added on top of the seller cost.",
      user: "Commission added to the bill amount for the seller.",
    },
  },
  jawwal_invoice: {
    title: "Jawwal Invoice Commission",
    intro:
      "Configure the commissions charged on a Jawwal invoice payment. The seller is charged: bill amount + user commission + system commission. Admin only.",
    descriptions: {
      system: "Platform service fee added on top of the seller cost.",
      user: "Commission added to the bill amount for the seller.",
    },
  },
  nedco: {
    title: "Nedco Commission",
    intro:
      "Configure the service fee on a Nedco voucher. The total fee deducted from the entered amount = system commission + user commission; the seller keeps the user commission. Admin only.",
    descriptions: {
      system: "Platform's share of the service fee.",
      user: "Seller's share of the service fee.",
    },
  },
};

const BillCommission = ({ provider }) => {
  const meta = PROVIDER_META[provider] || PROVIDER_META.mada;

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [editingName, setEditingName] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [savingName, setSavingName] = useState(null);

  useEffect(() => {
    fetchRows();
    // refetch when switching between provider pages
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const fetchRows = async () => {
    setIsLoading(true);
    try {
      const response = await ApiRequest.post(
        `bill_commission?operation=GET&provider=${provider}`
      );
      if (response?.data?.status === "success") {
        setRows(response.data.data || []);
      } else {
        Toast.fire({
          title: response?.data?.reason || "Failed to load commission settings",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching commission:", error);
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditingName(row.name);
    setEditedValue(String(row.commission));
  };

  const handleCancel = () => {
    setEditingName(null);
    setEditedValue("");
  };

  const handleSave = async (row) => {
    const commission = Number(editedValue);
    if (!Number.isFinite(commission) || commission < 0) {
      Toast.fire({ title: "Enter a valid commission (0 or more)", icon: "error" });
      return;
    }

    setSavingName(row.name);
    try {
      const response = await ApiRequest.post(
        `bill_commission?operation=UPDATE&provider=${provider}`,
        { name: row.name, commission },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response?.data?.status === "success") {
        Toast.fire({ title: "Commission updated successfully!", icon: "success" });
        setRows((prev) =>
          prev.map((r) => (r.name === row.name ? { ...r, commission } : r))
        );
        handleCancel();
      } else {
        Toast.fire({
          title: response?.data?.reason || "Failed to update commission",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error updating commission:", error);
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSavingName(null);
    }
  };

  return (
    <Box
      sx={{
        margin: "20px auto",
        padding: "30px",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
        borderRadius: "10px",
        color: "#000",
        width: "100%",
      }}
    >
      <Typography variant="h4" gutterBottom>
        {meta.title}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {meta.intro}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ marginTop: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell><strong>Commission</strong></TableCell>
                <TableCell><strong>Value (₪)</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const isEditing = editingName === row.name;
                const isSaving = savingName === row.name;

                return (
                  <TableRow key={row.name}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editedValue}
                          onChange={(e) => setEditedValue(e.target.value)}
                          inputProps={{ min: 0, step: "0.5" }}
                          sx={{ width: 120 }}
                          autoFocus
                        />
                      ) : (
                        <Typography variant="body2">
                          {formatNumber(row.commission)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {meta.descriptions[row.name] || ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {isEditing ? (
                        <>
                          <Tooltip title="Save">
                            <span>
                              <IconButton
                                color="primary"
                                onClick={() => handleSave(row)}
                                size="small"
                                disabled={isSaving}
                              >
                                {isSaving ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <SaveIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <span>
                              <IconButton
                                color="secondary"
                                onClick={handleCancel}
                                size="small"
                                disabled={isSaving}
                              >
                                <CancelIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(row)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No commission settings found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export const MadaCommission = () => <BillCommission provider="mada" />;
export const PaltelCommission = () => <BillCommission provider="paltel" />;
export const JawwalInvoiceCommission = () => (
  <BillCommission provider="jawwal_invoice" />
);
export const NedcoCommission = () => <BillCommission provider="nedco" />;

export default BillCommission;
